import { apiClient } from '@/lib/api-client';

export type UploadFolder =
  | 'listings/hero'
  | 'listings/before-after'
  | 'listings/renderings'
  | 'listings/floor-plans'
  | 'listings/renovation-options'
  | 'contractors/logo'
  | 'contractors/insurance'
  | 'contractors/license'
  | 'lenders/logo';

interface PresignedUrlRequest {
  filename: string;
  contentType: string;
  folder: UploadFolder;
  entityId?: string;
}

interface PresignedUrlResponse {
  uploadUrl: string;
  key: string; // S3 key to store in database
  fileUrl: string; // Full URL for display
  expiresIn: number;
}

interface UploadResult {
  key: string; // S3 key to store in database
  fileUrl: string; // Full URL for display
}

interface ApiWrapper<T> {
  result: T;
  status: number;
  message: string;
  error: null | { code: number; message: string; issues: unknown[] };
}

export const uploadService = {
  /**
   * Get a presigned URL for uploading a file to S3
   */
  async getPresignedUrl(request: PresignedUrlRequest): Promise<PresignedUrlResponse> {
    const response = await apiClient.post<ApiWrapper<PresignedUrlResponse>>(
      '/uploads/presigned-url',
      request
    );
    return response.result;
  },

  /**
   * Upload a file to S3 using a presigned URL
   * Returns both the S3 key (for storage) and the full URL (for display)
   */
  async uploadFile(file: File, folder: UploadFolder, entityId?: string): Promise<UploadResult> {
    // Get presigned URL
    const { uploadUrl, key, fileUrl } = await this.getPresignedUrl({
      filename: file.name,
      contentType: file.type,
      folder,
      entityId,
    });

    // Upload file directly to S3
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    // Return both key (for database) and fileUrl (for immediate display)
    return { key, fileUrl };
  },

  /**
   * Upload multiple files to S3
   * Returns both the S3 keys (for storage) and the full URLs (for display)
   */
  async uploadFiles(
    files: File[],
    folder: UploadFolder,
    entityId?: string
  ): Promise<UploadResult[]> {
    const uploadPromises = files.map((file) => this.uploadFile(file, folder, entityId));
    return Promise.all(uploadPromises);
  },
};
